/**
 * procedureScheduling
 *
 * Maps schedulable treatment-hub procedure steps (egg retrieval, transfer,
 * IUI, sperm collection) onto the app's canonical appointment-scheduling flow.
 *
 * Rather than a bespoke inline date/time form, an unscheduled procedure step
 * renders a canonical row that opens `AddAppointmentModal` pre-filled for that
 * procedure. On save, the chosen date is mirrored back onto the step's cycle
 * phase (via `handleUpdateStepProgress`) so the step flips to "scheduled" — the
 * same date→phase mechanism the two-week-wait beta-hCG card already uses.
 *
 * The appointment carries the active cycle ID, and the saved phase procedure
 * stores the appointment ID when the appointment is created. The date is still
 * mirrored onto the phase so the treatment-hub step updates immediately in the
 * current client state.
 */

/** The cycle-phase date field a reflected appointment date writes into. */
type ProcedureDateField = "procedureDate" | "transferDate" | "collectionDate";

type ProcedureSchedulingEntry = {
  /**
   * Canonical EN activity name — must match an entry in AddAppointmentModal's
   * PROCEDURE_SECTIONS (or round-trip as a free-text activity). Doubles as the
   * key used to detect the procedure back in `onSaved`'s consultationType.
   */
  activity: string;
  /** Pre-filled appointment title. */
  appointmentTitle: string;
  /** i18n key (under care.treatment_hub.schedule_procedure) for the row title. */
  rowTitleKey: string;
  dateField: ProcedureDateField;
};

const PROCEDURE_SCHEDULING: Record<string, ProcedureSchedulingEntry> = {
  egg_retrieval: {
    activity: "Egg retrieval",
    appointmentTitle: "Egg retrieval",
    rowTitleKey: "care.treatment_hub.schedule_procedure.egg_retrieval_title",
    dateField: "procedureDate",
  },
  transfer: {
    activity: "Embryo transfer",
    appointmentTitle: "Embryo transfer",
    rowTitleKey: "care.treatment_hub.schedule_procedure.transfer_title",
    dateField: "transferDate",
  },
  sperm_collection: {
    activity: "Sperm collection",
    appointmentTitle: "Sperm collection",
    rowTitleKey: "care.treatment_hub.schedule_procedure.sperm_collection_title",
    dateField: "collectionDate",
  },
  insemination: {
    activity: "Intrauterine insemination (IUI)",
    appointmentTitle: "IUI",
    rowTitleKey: "care.treatment_hub.schedule_procedure.insemination_title",
    dateField: "procedureDate",
  },
};

export type ProcedureAppointmentPrefill = {
  type: "procedure";
  activities: string[];
  title: string;
};

/** The scheduling metadata for a step, or null if the step is not a
 * canonical schedulable procedure that maps to the appointment flow. */
export function getProcedureSchedulingEntry(
  stepId: string,
): ProcedureSchedulingEntry | null {
  return PROCEDURE_SCHEDULING[stepId] ?? null;
}

/** The AddAppointmentModal `prefill` payload for a schedulable procedure step. */
export function getProcedureAppointmentPrefill(
  stepId: string,
): ProcedureAppointmentPrefill | null {
  const entry = PROCEDURE_SCHEDULING[stepId];
  if (!entry) return null;
  return {
    type: "procedure",
    activities: [entry.activity],
    title: entry.appointmentTitle,
  };
}

/**
 * Reverse lookup: given the activities a saved appointment carries, find the
 * procedure step it schedules (so its cycle phase can be updated). Returns the
 * step id + the phase date field to write.
 */
export function findScheduledProcedureStep(
  consultationType: string[] | null | undefined,
): { stepId: string; dateField: ProcedureDateField } | null {
  const normalized = (consultationType ?? []).map((a) =>
    a.trim().toLowerCase(),
  );
  for (const [stepId, entry] of Object.entries(PROCEDURE_SCHEDULING)) {
    if (normalized.includes(entry.activity.toLowerCase())) {
      return { stepId, dateField: entry.dateField };
    }
  }
  return null;
}
